import AddIcon from '@mui/icons-material/Add';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import ClearIcon from '@mui/icons-material/Clear';
import { Fab, Popover, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { useNavigate } from 'react-router-dom';
import { LatLng } from '../../../../firebase/firebase.types';
import { PlacePrediction } from '../../../../hooks/usePlacesAutocomplete';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectCurrentTrip, selectTripCreationStatus } from '../../../../redux/trip/trip.selectors';
import { createTrip, resetTripCreationStatus } from '../../../../redux/trip/trip.slice';
import { TripCreationStatus } from '../../../../redux/trip/trip.types';
import { selectCurrentUser } from '../../../../redux/user/user.selectors';
import { getISODate } from '../../../../redux/utils';
import { getDetails } from '../../../../utils/places-api.utils';
import { Button, Modal } from '../../../shared';
import 'react-day-picker/dist/style.css';
import PlacesAutocomplete from '../../../PlacesAutocomplete/PlacesAutocomplete';
import './CreateTripModal.scss';

function devLog(message: string, data?: unknown) {
  if (process.env.NODE_ENV === 'development') {
    // biome-ignore lint/suspicious/noConsole: should remove these
    console.log(message, data);
  }
}

function hasGeometry(
  result: google.maps.places.PlaceResult | string
): result is google.maps.places.PlaceResult {
  return (result as google.maps.places.PlaceResult).geometry !== undefined;
}

type Place = {
  description: string;
  location: LatLng;
};

type DateFieldProps = {
  label: string;
  value?: Date;
  onChange: (date?: Date) => void;
  disabledBefore?: Date;
  defaultMonth?: Date;
};

function DateField({ label, value, onChange, disabledBefore, defaultMonth }: DateFieldProps) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <div className='create-trip-field'>
      <span className='create-trip-label'>{label}</span>
      <button
        ref={anchorRef}
        type='button'
        className='create-trip-date-input'
        onClick={() => setOpen(true)}
      >
        <CalendarTodayRoundedIcon />
        <span className={value ? '' : 'create-trip-date-placeholder'}>
          {value ? format(value, 'MMM d, yyyy') : 'Select date'}
        </span>
      </button>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <DayPicker
          mode='single'
          selected={value}
          defaultMonth={value ?? defaultMonth}
          disabled={disabledBefore ? { before: disabledBefore } : undefined}
          onSelect={(date) => {
            onChange(date);
            setOpen(false);
          }}
        />
      </Popover>
    </div>
  );
}

function CreateTripModal() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentTrip = useAppSelector(selectCurrentTrip);
  const tripCreationStatus = useAppSelector(selectTripCreationStatus);

  const [place, setPlace] = useState<Place | null>(null);
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [hasError, setHasError] = useState(false);
  const [isResolvingPlace, setIsResolvingPlace] = useState(false);
  const [createTripModalOpen, setCreateTripModalOpen] = useState(false);

  const handleModalToggle = useCallback(() => {
    setCreateTripModalOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setCreateTripModalOpen(false);
    setPlace(null);
    setStartDate(undefined);
    setEndDate(undefined);
    setHasError(false);
    setIsResolvingPlace(false);
  }, []);

  const handleStartDateChange = (date?: Date) => {
    setStartDate(date);
    if (date && endDate && endDate < date) {
      setEndDate(undefined);
    }
  };

  useEffect(() => {
    if (tripCreationStatus === TripCreationStatus.CREATED) {
      dispatch(resetTripCreationStatus());
      if (currentTrip) {
        navigate(`/trips/${currentTrip.id}`);
      }
      handleClose(); // Close modal after creating trip
    }
  }, [currentTrip, dispatch, navigate, tripCreationStatus, handleClose]);

  const handleSuggestionSelect = (
    suggestion: PlacePrediction,
    sessionToken: string,
    usedNewApi: boolean
  ) => {
    const { place_id, description } = suggestion;

    // Only use session token if New API was used for autocomplete
    const sessionTokenToUse = usedNewApi ? sessionToken : undefined;

    setPlace(null);
    setIsResolvingPlace(true);
    getDetails({
      placeId: place_id,
      fields: ['geometry.location'],
      sessionToken: sessionTokenToUse,
    })
      .then((details) => {
        if (hasGeometry(details) && details?.geometry?.location) {
          const latitude = details.geometry.location.lat();
          const longitude = details.geometry.location.lng();
          setPlace({
            description,
            location: { latitude, longitude },
          });
          setHasError(false);
        }
      })
      .catch((error) => {
        devLog('Error ', error);
      })
      .finally(() => setIsResolvingPlace(false));
  };

  const handleStartPlanning = () => {
    if (place) {
      if (currentUser) {
        // A single chosen date makes a one-day trip, since a trip's dates are always a range.
        const from = startDate ?? endDate;
        const to = endDate ?? startDate;
        dispatch(
          createTrip({
            place: place.description,
            location: place.location,
            user: currentUser,
            startDateISO: from && getISODate(from),
            endDateISO: to && getISODate(to),
          })
        )
          .unwrap()
          .catch((error) => devLog('createTrip failed', error));
      } else {
        devLog('createTrip skipped: no current user');
      }
      setHasError(false);
    } else {
      setHasError(true);
    }
  };

  return (
    <div className='create-trip-container'>
      <Modal
        open={createTripModalOpen}
        onClose={handleClose}
        size='md'
        title='Create Trip'
        showCloseButton={false}
        className='create-trip-modal'
        footer={
          <>
            <Button
              tone='plain'
              quiet
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              outline
              tone='neutral'
              onClick={handleStartPlanning}
              disabled={isResolvingPlace}
            >
              Create Trip
            </Button>
          </>
        }
      >
        <p className='create-trip-required'>
          <span aria-hidden='true'>*</span>Required
        </p>
        <div className='create-trip-field'>
          <span className='create-trip-label'>
            Where are you going? <span className='create-trip-asterisk'>*</span>
          </span>
          <PlacesAutocomplete
            types={['(regions)']}
            placeholder='E.g. Kyoto, Japan or Amalfi Coast, Italy'
            className='tripsList-autocomplete'
            onSuggestionSelect={handleSuggestionSelect}
            hasError={hasError}
            onInputChange={(value) => {
              if (!value) {
                setHasError(false);
              }
            }}
            showSearchIcon
          />
          {hasError && (
            <span
              className='create-trip-error'
              role='alert'
            >
              Location is required
            </span>
          )}
        </div>
        <div className='create-trip-dates'>
          <DateField
            label='Start Date'
            value={startDate}
            onChange={handleStartDateChange}
          />
          <DateField
            label='End Date'
            value={endDate}
            onChange={setEndDate}
            disabledBefore={startDate}
            defaultMonth={startDate}
          />
        </div>
      </Modal>
      <Fab
        className={`toggle-modal-button${createTripModalOpen ? ' open' : ''}`}
        onClick={createTripModalOpen ? handleClose : handleModalToggle}
      >
        {createTripModalOpen ? (
          <ClearIcon />
        ) : (
          <Tooltip
            title='Create a Trip'
            placement='top'
          >
            <AddIcon />
          </Tooltip>
        )}
      </Fab>
    </div>
  );
}

CreateTripModal.displayName = 'CreateTripModal';
export default CreateTripModal;
